import React, { useState } from "react";
import { useToast } from "../context/ToastContext";
import "./ToolPage.css";
import flaskApi from "../services/flaskApi";
import { extractFilenameFromHeader } from "../utils/fileUtils";
import { extractErrorMessage } from "../utils/errorUtils";
import ToolInfoModal from "../components/common/ToolInfoModal";
import ToolFileUploadField from "./shared/ToolFileUploadField";
import InventorySourceSelector from "./shared/InventorySourceSelector";
import ToolPageLayout from "./shared/ToolPageLayout";
import ToolProgressPanel from "./shared/ToolProgressPanel";


function DisplaySheetTool() {
  const [salesFloorFile, setSalesFloorFile] = useState(null);
  
  const [inventorySource, setInventorySource] = useState("upload");
  const [inventoryShopifyConfig, setInventoryShopifyConfig] = useState({
    location: "Main Store",
    vendors: "",
    excludeArchive: true,
    excludeRS: true
  });
  const [inventoryFile, setInventoryFile] = useState(null);
  const [fetchingShopify, setFetchingShopify] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { addToast } = useToast();
  const [showInfoModal, setShowInfoModal] = useState(false);

  const toolInfo = {
    toolName: "Display Sheet",
    description: "Compare sales floor data with inventory to determine the display status of items.",
    steps: [
      "Upload your Sales Floor file (Excel) containing UPC codes.",
      "Upload your Inventory file (Excel).",
      "Click 'Display Sheet' to see which items are on display versus backstock.",
    ],
    templates: [
      { name: "Sales Floor Template", link: "/templates/download/template_sales_floor.xlsx" },
      { name: "Inventory Template", link: "/templates/download/template_inventory.xlsx" },
    ],
  };

  const guideTips = [
    "Use the latest sales floor export so the display comparison reflects what is currently on the floor.",
    "The inventory file should come from the same timeframe as the sales floor file for the cleanest result.",
    "This output is best for quickly separating display items from backstock without checking each SKU manually.",
  ];

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!salesFloorFile) {
      addToast("Please upload the sales floor file.", "error");
      return;
    }
    if (inventorySource === "upload" && !inventoryFile) {
      addToast("Please upload the inventory file.", "error");
      return;
    }

    const maxSize = 100 * 1024 * 1024;
    if (salesFloorFile.size > maxSize || (inventorySource === "upload" && inventoryFile && inventoryFile.size > maxSize)) {
      addToast("One or more files are too large. Maximum size is 100MB.", "error");
      return;
    }
    
    let finalInventoryFile = inventoryFile;
    
    if (inventorySource === "shopify") {
      setFetchingShopify(true);
      try {
        const excludeTags = [];
        if (inventoryShopifyConfig.excludeArchive) excludeTags.push("Archive");
        if (inventoryShopifyConfig.excludeRS) excludeTags.push("RS");
        
        const vendors = inventoryShopifyConfig.vendors
          ? inventoryShopifyConfig.vendors.split(",").map(v => v.trim()).filter(v => v)
          : [];
          
        const locationNames = inventoryShopifyConfig.location === "All" 
          ? [] 
          : [inventoryShopifyConfig.location];

        const shopifyRes = await flaskApi.post(
          "/shopify/fetch-inventory-excel", 
          { vendors, exclude_tags: excludeTags, location_names: locationNames },
          { responseType: "blob" }
        );
        
        finalInventoryFile = new File([shopifyRes.data], "shopify_inventory.xlsx", {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });
        
      } catch (err) {
        setFetchingShopify(false);
        const errorMessage = await extractErrorMessage(err, "Failed to fetch inventory from Shopify.");
        addToast(errorMessage, "error");
        return;
      }
      setFetchingShopify(false);
    }

    const formData = new FormData();
    formData.append("sales_floor_file", salesFloorFile);
    formData.append("inventory_file", finalInventoryFile);

    setLoading(true);
    setUploadProgress(0);

    try {
      const response = await flaskApi.post("/product/generate-display-sheet-ui", formData, {
        responseType: "blob",
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });

      if (
        response.data.type === "application/json" ||
        (response.headers["content-type"] &&
          response.headers["content-type"].includes("application/json"))
      ) {
        const text = await response.data.text();
        const json = JSON.parse(text);
        addToast(`Error: ${json.error}`, "error");
        return;
      }

      if (
        response.data.type === "text/html" ||
        (response.headers["content-type"] &&
          response.headers["content-type"].includes("text/html"))
      ) {
        const text = await response.data.text();
        console.error("Received HTML response:", text);
        addToast("Server Error: Received HTML instead of file.", "error");
        return;
      }

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const contentDisposition = response.headers["content-disposition"] || response.headers["Content-Disposition"];
      const extractedFilename = extractFilenameFromHeader(contentDisposition);
      const filename = extractedFilename || "Display_Sheet.xlsx";

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      addToast("File downloaded successfully.", "success");
    } catch (error) {
      console.error(error);
      const errorMessage = await extractErrorMessage(error, "Error generating display sheet.");
      addToast(errorMessage, "error");
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  return (
    <>
      <ToolPageLayout
        title="Display Sheet"
        subtitle={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
        tips={guideTips}
        onOpenInfo={() => setShowInfoModal(true)}
      >
        <form onSubmit={handleSubmit} className="tool-form tool-form-split">
          <div className="tool-form-main">
            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Sales Floor File</h3>
              </div>
              <ToolFileUploadField
                label="Sales Floor File"
                file={salesFloorFile}
                setFile={setSalesFloorFile}
              />
            </section>

            <div className="tool-action-stack">
              <button type="submit" className="tool-btn-primary" disabled={loading || fetchingShopify}>
                {loading || fetchingShopify ? "Processing..." : "Display Sheet"}
              </button>

              <ToolProgressPanel
                loading={loading}
                isDownloadingShopify={fetchingShopify}
                uploadProgress={uploadProgress}
                uploadMessage="Uploading sales floor and inventory files..."
                processingMessage="Generating Display Sheet..."
              />
            </div>
          </div>

          <div className="tool-form-side">
            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Inventory Source</h3>
              </div>
              <InventorySourceSelector
                source={inventorySource}
                onSourceChange={setInventorySource}
                uploadFile={inventoryFile}
                onUploadFileChange={setInventoryFile}
                shopifyConfig={inventoryShopifyConfig}
                onShopifyConfigChange={setInventoryShopifyConfig}
              />
            </section>
          </div>
        </form>
      </ToolPageLayout>

      <ToolInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        toolName={toolInfo.toolName}
        description={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
      />
    </>
  );
}


export default DisplaySheetTool;
